// AI Shopping Assistant - "Burt"
// Smart suggestions, price tracking, and shopping intelligence

/**
 * Analyzes shopping history and suggests items to add
 * @param {Array} previousShoppingList - Past shopping items
 * @param {Array} currentList - Current shopping list
 * @returns {Array} - Suggested items with reasoning
 */
export function getSuggestedItems(previousShoppingList = [], currentList = []) {
  const suggestions = [];
  
  // Common staples — fresh items → Walmart (in-store), pantry/household → Amazon (affiliate revenue)
  const staples = [
    // Fresh items — Walmart makes sense for these (perishables, in-store)
    { name: 'Milk', category: 'Dairy', avgPrice: 3.50, frequency: 7, store: 'walmart' },
    { name: 'Eggs', category: 'Dairy', avgPrice: 3.99, frequency: 7, store: 'walmart' },
    { name: 'Bread', category: 'Bakery', avgPrice: 2.50, frequency: 5, store: 'walmart' },
    { name: 'Bananas', category: 'Produce', avgPrice: 0.59, frequency: 5, store: 'walmart' },
    { name: 'Chicken Breast', category: 'Meat', avgPrice: 8.99, frequency: 7, store: 'walmart' },
    // Pantry/dry goods — Amazon earns affiliate commission on these
    { name: 'White Rice (5lb)', category: 'Pantry', avgPrice: 7.99, frequency: 30, store: 'amazon' },
    { name: 'Pasta (2-pack)', category: 'Pantry', avgPrice: 4.99, frequency: 14, store: 'amazon' },
    { name: 'Olive Oil', category: 'Pantry', avgPrice: 9.99, frequency: 30, store: 'amazon' },
    { name: 'Canned Tomatoes', category: 'Pantry', avgPrice: 5.99, frequency: 14, store: 'amazon' },
    { name: 'Peanut Butter', category: 'Pantry', avgPrice: 6.99, frequency: 21, store: 'amazon' },
    { name: 'Oatmeal', category: 'Pantry', avgPrice: 5.49, frequency: 14, store: 'amazon' },
    { name: 'Honey', category: 'Pantry', avgPrice: 8.99, frequency: 60, store: 'amazon' },
    // Household — Amazon typically has better bulk prices here
    { name: 'Paper Towels', category: 'Household', avgPrice: 18.99, frequency: 30, store: 'amazon' },
    { name: 'Dish Soap', category: 'Household', avgPrice: 7.99, frequency: 45, store: 'amazon' },
    { name: 'Laundry Detergent', category: 'Household', avgPrice: 14.99, frequency: 30, store: 'amazon' },
    { name: 'Trash Bags', category: 'Household', avgPrice: 12.99, frequency: 45, store: 'amazon' },
    { name: 'Ziploc Bags', category: 'Household', avgPrice: 9.99, frequency: 45, store: 'amazon' },
  ];

  const currentItemNames = currentList.map(item => item?.name?.toLowerCase() || '').filter(name => name);

  // Suggest staples not in current list
  staples.forEach(staple => {
    if (!currentItemNames.includes(staple.name.toLowerCase())) {
      suggestions.push({
        ...staple,
        price: staple.avgPrice,
        reason: `Usually purchased every ${staple.frequency} days`,
        confidence: 0.8,
        quantity: 1
      });
    }
  });

  return suggestions.slice(0, 5); // Return top 5 suggestions
}

/**
 * Compare prices between Amazon and Walmart
 * @param {string} itemName - Item to compare
 * @returns {Object} - Price comparison data
 */
export function comparePrices(itemName) {
  // This will be enhanced with real API data
  // For now, return mock comparison
  const mockPrices = {
    amazon: {
      price: (Math.random() * 20 + 5).toFixed(2),
      shipping: 'Free with Prime',
      delivery: '1-2 days'
    },
    walmart: {
      price: (Math.random() * 20 + 5).toFixed(2),
      shipping: 'Free pickup',
      delivery: 'Today'
    }
  };

  const cheaper = parseFloat(mockPrices.amazon.price) < parseFloat(mockPrices.walmart.price) ? 'amazon' : 'walmart';
  const savings = Math.abs(parseFloat(mockPrices.amazon.price) - parseFloat(mockPrices.walmart.price)).toFixed(2);

  return {
    ...mockPrices,
    cheaper,
    savings,
    recommendation: cheaper === 'amazon' ? '🟧 Buy on Amazon' : '🟦 Buy at Walmart'
  };
}

/**
 * Organize Walmart items by aisle/department
 * @param {Array} items - Shopping list items
 * @returns {Object} - Items organized by category
 */
export function organizeByAisle(items) {
  const aisleMap = {
    'Dairy': { aisle: 12, order: 1 },
    'Produce': { aisle: 1, order: 2 },
    'Meat': { aisle: 8, order: 3 },
    'Bakery': { aisle: 3, order: 4 },
    'Pantry': { aisle: 6, order: 5 },
    'Canned Goods': { aisle: 7, order: 6 },
    'Frozen': { aisle: 15, order: 7 },
    'Household': { aisle: 20, order: 8 },
    'Personal Care': { aisle: 18, order: 9 },
    'Other': { aisle: 99, order: 10 }
  };

  const organized = {};
  
  items.forEach(item => {
    const category = item.category || 'Other';
    const aisleInfo = aisleMap[category] || aisleMap['Other'];
    
    if (!organized[category]) {
      organized[category] = {
        aisle: aisleInfo.aisle,
        order: aisleInfo.order,
        items: []
      };
    }
    
    organized[category].items.push(item);
  });

  // Sort by order
  return Object.entries(organized)
    .sort((a, b) => a[1].order - b[1].order)
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
}

/**
 * Get budget recommendations
 * @param {number} currentTotal - Current cart total
 * @param {number} budget - Weekly budget
 * @returns {Object} - Budget advice
 */
export function getBudgetAdvice(currentTotal, budget) {
  const percentage = (currentTotal / budget) * 100;
  const remaining = budget - currentTotal;

  let status = 'good';
  let message = `You're doing great! ${remaining.toFixed(2)} left in your budget.`;
  let emoji = '✅';

  if (percentage >= 100) {
    status = 'over';
    message = `You're over budget by $${Math.abs(remaining).toFixed(2)}. Consider removing some items.`;
    emoji = '⚠️';
  } else if (percentage >= 90) {
    status = 'warning';
    message = `Getting close to your budget! Only $${remaining.toFixed(2)} remaining.`;
    emoji = '⚡';
  } else if (percentage >= 75) {
    status = 'approaching';
    message = `You've used 75% of your budget. $${remaining.toFixed(2)} left.`;
    emoji = '👀';
  }

  return {
    status,
    message,
    emoji,
    percentage,
    remaining
  };
}

/**
 * Smart item categorization
 * @param {string} itemName - Name of the item
 * @returns {string} - Category name
 */
export function smartCategorize(itemName) {
  const name = itemName.toLowerCase();
  
  const categories = {
    'Dairy': ['milk', 'cheese', 'butter', 'yogurt', 'cream', 'eggs'],
    'Produce': ['apple', 'banana', 'orange', 'lettuce', 'tomato', 'potato', 'onion', 'carrot'],
    'Meat': ['chicken', 'beef', 'pork', 'fish', 'turkey', 'bacon', 'sausage'],
    'Bakery': ['bread', 'bagel', 'roll', 'donut', 'cake', 'muffin'],
    'Pantry': ['rice', 'pasta', 'flour', 'sugar', 'oil', 'cereal'],
    'Canned Goods': ['soup', 'beans', 'sauce', 'tuna', 'corn'],
    'Frozen': ['ice cream', 'pizza', 'frozen', 'popsicle'],
    'Household': ['paper towel', 'tissue', 'cleaner', 'detergent', 'soap'],
    'Personal Care': ['shampoo', 'toothpaste', 'deodorant', 'lotion']
  };

  for (const [category, keywords] of Object.entries(categories)) {
    if (keywords.some(keyword => name.includes(keyword))) {
      return category;
    }
  }

  return 'Other';
}

/**
 * Generate shopping motivation messages from Burt
 * @returns {string} - Motivational message
 */
export function getBurtMotivation() {
  const messages = [
    "🤖 Hey! Smart shopping saves money for family fun!",
    "🤖 You're doing great! Let's stick to the list!",
    "🤖 Remember: Hungry shopping = overspending. Eat first!",
    "🤖 Pro tip: Check for coupons before you checkout!",
    "🤖 Nice work planning ahead! Your wallet will thank you.",
    "🤖 Smart shopping = more allowance for the kids! 💰",
    "🤖 Don't forget to compare prices between stores!",
    "🤖 Buying in bulk? Make sure you have storage space!",
    "🤖 Generic brands can save 20-40% without sacrificing quality!",
    "🤖 Shopping on a full stomach? You're already winning!"
  ];

  return messages[Math.floor(Math.random() * messages.length)];
}

/**
 * Analyze shopping patterns
 * @param {Array} history - Past shopping lists
 * @returns {Object} - Shopping insights
 */
export function analyzeShoppingPatterns(history = []) {
  const insights = {
    topItems: [],
    avgSpending: 0,
    mostFrequentStore: 'walmart',
    savingsTips: []
  };

  if (history.length === 0) {
    insights.savingsTips = [
      "Start tracking your shopping to get personalized insights!",
      "Set a weekly budget to stay on track",
      "Compare prices between Amazon and Walmart"
    ];
    return insights;
  }

  // Calculate average spending
  const totalSpent = history.reduce((sum, list) => {
    return sum + (list.items || []).reduce((itemSum, item) => itemSum + (item.price * item.quantity || 0), 0);
  }, 0);
  insights.avgSpending = (totalSpent / history.length).toFixed(2);

  // Find most purchased items
  const itemCounts = {};
  history.forEach(list => {
    (list.items || []).forEach(item => {
      itemCounts[item.name] = (itemCounts[item.name] || 0) + 1;
    });
  });
  
  insights.topItems = Object.entries(itemCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  // Generate savings tips
  insights.savingsTips = [
    `💡 You buy ${insights.topItems[0]?.name} often. Buy in bulk to save!`,
    `💰 Your average spending is $${insights.avgSpending}/week`,
    `🎯 Set a goal to reduce spending by 10% this month`
  ];

  return insights;
}


